import React, { useEffect, useRef, useState } from 'react';
import { Play, Loader2, ChevronDown, ChevronUp, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminCodeHealthAPI } from '../services/api';

const ICON = {
  passed: <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />,
  failure: <XCircle size={13} className="text-red-600 shrink-0" />,
  error: <XCircle size={13} className="text-red-600 shrink-0" />,
  skipped: <MinusCircle size={13} className="text-gray-400 shrink-0" />,
};

const fmtDuration = (s) => (s >= 60 ? `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s` : `${s} s`);

// Groups junit cases by file, failures first
const groupCases = (cases = []) => {
  const map = new Map();
  cases.forEach((c) => { if (!map.has(c.file)) map.set(c.file, []); map.get(c.file).push(c); });
  return [...map.entries()].sort(([, a], [, b]) => b.some((c) => c.status !== 'passed' && c.status !== 'skipped') - a.some((c) => c.status !== 'passed' && c.status !== 'skipped'));
};

const FileGroup = ({ file, cases, suiteKey }) => {
  const bad = cases.filter((c) => c.status === 'failure' || c.status === 'error').length;
  const [open, setOpen] = useState(bad > 0);
  return (
    <li className="py-1.5" data-testid={`suite-${suiteKey}-file-${file}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="flex items-center gap-1.5 min-w-0">{bad ? ICON.failure : ICON.passed}<code className="text-xs truncate">{file}</code></span>
        <span className="flex items-center gap-2 text-xs text-gray-600 shrink-0">{cases.length - bad}/{cases.length}{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>
      {open && (
        <ul className="mt-1 ml-5 space-y-0.5">
          {cases.map((c) => (
            <li key={c.name} className="text-xs flex items-start gap-1.5" data-testid={`suite-case-${c.status}`}>
              {ICON[c.status]}
              <span className={c.status === 'failure' || c.status === 'error' ? 'text-red-700' : 'text-gray-700'}>
                {c.name} <span className="text-gray-400">· {c.time} s</span>
                {c.message && <span className="block text-[11px] text-red-600 break-all">{c.message}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

// One runnable test suite (e.g. fitting 52→76) with live polling and per-test detail
const TestSuitePanel = ({ suite }) => {
  const [detail, setDetail] = useState(null);
  const [running, setRunning] = useState(suite.running);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = () => adminCodeHealthAPI.suite(suite.key).then((d) => {
    setDetail(d); setRunning(d.running);
    if (d.running) timer.current = setTimeout(load, 5000);
  }).catch(() => {});
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setRunning(true);
    try {
      const r = await adminCodeHealthAPI.runSuite(suite.key);
      toast.success(r.started ? `Suite lancée (${r.files} fichiers, 3 à 5 minutes)` : 'Suite déjà en cours');
      timer.current = setTimeout(load, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Lancement impossible'); setRunning(false); }
  };

  const last = detail?.at ? detail : null;
  const failed = last ? (last.failure || 0) + (last.error || 0) : 0;
  const ok = last?.ran && !failed;
  return (
    <div className="rounded-lg border border-gray-100 bg-white/70 px-3 py-2.5" data-testid={`suite-${suite.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{suite.label}</span>
            {last && last.ran && <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid={`suite-${suite.key}-summary`}>{last.passed}/{last.tests - (last.skipped || 0)} réussis{last.skipped ? ` · ${last.skipped} ignoré(s)` : ''}{failed ? ` · ${failed} en échec` : ''}</span>}
            {last && !last.ran && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`suite-${suite.key}-error`}>Non exécutée : {last.error}</span>}
            {running && <Loader2 size={14} className="animate-spin text-gray-500" />}
          </div>
          <p className="text-xs text-gray-600 mt-0.5">{suite.description}</p>
          <p className="text-[11px] text-gray-500 mt-0.5" data-testid={`suite-${suite.key}-at`}>
            {last ? `Dernier passage : ${new Date(last.at).toLocaleString('fr-FR')} · ${fmtDuration(last.duration || 0)} · ${suite.files} fichiers` : `Jamais lancée · ${suite.files} fichiers`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {last?.cases?.length > 0 && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-gray-700 hover:text-gray-900 inline-flex items-center gap-1" data-testid={`suite-${suite.key}-toggle`}>
              Détail {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
          <button type="button" onClick={run} disabled={running} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50" data-testid={`suite-${suite.key}-run`}>
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} {running ? 'En cours…' : 'Lancer'}
          </button>
        </div>
      </div>
      {open && last?.cases?.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100" data-testid={`suite-${suite.key}-detail`}>
          {groupCases(last.cases).map(([file, cases]) => <FileGroup key={file} file={file} cases={cases} suiteKey={suite.key} />)}
        </ul>
      )}
    </div>
  );
};

export default TestSuitePanel;
